import React from 'react';
import { ArrowRight, Ship, Anchor, Waves, Shield, MapPin, DollarSign, CheckCircle } from 'lucide-react';
import SEO from '../components/SEO';

const BoatRVStorage = () => {
  const benefits = [
    'Minutes from Lake Texoma',
    'Close to Bois d\'Arc Lake',
    'Wide gravel lots for easy maneuvering',
    'No size restrictions',
    '24/7 access for spontaneous lake trips',
    'Secure gated facility',
    'Lower cost than marina storage',
    'Perfect for long-term or seasonal storage'
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Boat & RV Storage Near Lake Texoma | VD Storage Bells"
        description="Affordable boat and RV storage near Lake Texoma and Bois d'Arc Lake in Bells, TX. 24/7 access, secure gated facility, gravel lots for easy maneuvering."
        canonical="/boat-rv-storage-bells"
      />
      {/* Header */}
      <section className="bg-green-600 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4" data-testid="boat-rv-title">
            Boat & RV Storage in Bells, TX
          </h1>
          <p className="text-xl text-green-100 max-w-3xl mx-auto">
            Storage for boats, RVs, trailers, and watercraft near Lake Texoma
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">
            <p className="text-xl text-gray-700 leading-relaxed mb-6">
              Store your boat or RV at VD Storage Bells and be on the water at Lake Texoma or Bois d'Arc Lake
              in minutes. Our gravel lots provide secure, affordable storage without the hassle and
              expense of marina fees or HOA restrictions.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              Whether you own a bass boat, pontoon, sailboat, jet ski, travel trailer, or motorhome, we have
              the space you need. Pull in anytime with 24/7 access and hit the lake when the fish are biting.
            </p>
          </div>
        </div>
      </section>

      {/* Perfect For */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Perfect For
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-white p-8 rounded-xl shadow-md text-center">
              <Ship className="h-12 w-12 text-green-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Boats of All Sizes</h3>
              <p className="text-gray-600">
                Bass boats, pontoons, sailboats, fishing boats
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-md text-center">
              <Waves className="h-12 w-12 text-green-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Watercraft</h3>
              <p className="text-gray-600">
                Jet skis, wave runners, kayaks with trailers
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-md text-center">
              <Anchor className="h-12 w-12 text-green-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-3">RVs & Trailers</h3>
              <p className="text-gray-600">
                Travel trailers, fifth wheels, motorhomes
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-md text-center">
              <Ship className="h-12 w-12 text-green-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Campers</h3>
              <p className="text-gray-600">
                Pop-ups, truck campers, toy haulers
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Store With Us?
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-gray-50 p-6 rounded-lg text-center">
              <MapPin className="h-10 w-10 text-green-600 mx-auto mb-3" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Lake Access</h3>
              <p className="text-gray-600 text-sm">
                Minutes from Texoma and Bois d'Arc
              </p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg text-center">
              <Shield className="h-10 w-10 text-green-600 mx-auto mb-3" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Secure</h3>
              <p className="text-gray-600 text-sm">
                Gated facility with fenced perimeter
              </p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg text-center">
              <DollarSign className="h-10 w-10 text-green-600 mx-auto mb-3" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Affordable</h3>
              <p className="text-gray-600 text-sm">
                Lower rates than marina storage
              </p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg text-center">
              <CheckCircle className="h-10 w-10 text-green-600 mx-auto mb-3" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Easy Access</h3>
              <p className="text-gray-600 text-sm">
                Wide gravel lots, 24/7 availability
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits List */}
      <section className="py-16 bg-green-50 border-t border-b border-green-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">
            Additional Benefits
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex items-center space-x-3">
                <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0" />
                <span className="text-lg text-gray-700">{benefit}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gray-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Store Your Boat or RV?</h2>
          <p className="text-xl text-gray-300 mb-8">
            Contact us today for pricing and availability.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/contact"
              className="inline-flex items-center px-8 py-3 text-lg font-semibold text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors shadow-lg btn-primary"
              data-testid="contact-button"
            >
              Contact Us
              <ArrowRight className="ml-2 h-5 w-5" />
            </a>
            <a
              href="/storage-sizes"
              className="inline-flex items-center px-8 py-3 text-lg font-semibold text-gray-900 bg-white rounded-lg hover:bg-gray-100 transition-colors shadow-lg"
              data-testid="sizes-button"
            >
              View Sizes & Rates
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BoatRVStorage;